import { useState, useEffect } from 'react';
import i18n from '@/i18n';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/modules/auth';

interface UserInfo {
  id: string;
  auth_user_id: string;
  email: string;
  full_name: string;
  role: string;
}

interface UseUserLookupReturn {
  getUserName: (authUserId: string) => string | null;
  addUserToCache: (authUserId: string, userInfo: UserInfo) => void;
  loading: boolean;
  error: string | null;
}

export function useUserLookup(): UseUserLookupReturn {
  const [userCache, setUserCache] = useState<Map<string, UserInfo>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { tenantId } = useAuth();

  const fetchUserInfo = async (authUserId: string): Promise<UserInfo | null> => {
    try {
      setLoading(true);
      setError(null);

      // Query public.users directly (NOT tenant schema users)
      // Verified in cloud production: public.users has auth_user_id, full_name, email, role
      const { data, error: userError } = await supabase
        .from('users')
        .select('id, auth_user_id, email, full_name, role')
        .eq('auth_user_id', authUserId)
        .single();

      if (userError) {
        console.warn('[useUserLookup] Failed to fetch user:', userError);
        return null;
      }

      if (data) {
        const userInfo: UserInfo = {
          id: data.id,
          auth_user_id: data.auth_user_id,
          email: data.email,
          full_name: data.full_name,
          role: data.role
        };

        // Cache the user info
        setUserCache(prev => new Map(prev).set(authUserId, userInfo));
        return userInfo;
      }

      return null;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : i18n.t('conversations:hookErrors.fetchUserInfoFailed');
      console.error('[useUserLookup] Error:', errorMessage);
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (authUserId: string): string | null => {
    // Check cache first
    const cachedUser = userCache.get(authUserId);
    if (cachedUser) {
      return cachedUser.full_name || cachedUser.email;
    }

    // Don't fetch during render - return null and let effect handle it
    return null;
  };

  const addUserToCache = (authUserId: string, userInfo: UserInfo) => {
    setUserCache(prev => new Map(prev).set(authUserId, userInfo));
  };

  return {
    getUserName,
    addUserToCache,
    loading,
    error
  };
}